import Search from '@atoms/Search/Search';
import Text from '@atoms/Text/Text';
import ListingTypeEnum from '@models/enums/ListingTypeEnum';
import SiteEnum from '@models/enums/SiteEnum';
import { IListingFilterGroup } from '@models/IListing';
import { QueryState } from '@models/IListingWrapper';
import ListingFilters from '@molecules/ListingFilters/ListingFilters';
import {
  useListingDispatch,
  useListingState,
} from '@utility/context/ListingContext';
import { ActionType } from '@utility/reducers/ListingWrapper';
import classNames from 'classnames';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';

interface ListingHeaderProps {
  readonly autoCompleteEndpoint: string;
  readonly autoCompletePlaceholder?: string;
  readonly errors: any;
  readonly initialSearchText?: string;
  readonly listingFilterParam?: string;
  readonly listingFilters?: IListingFilterGroup[];
  readonly queryState: QueryState;
  readonly register: UseFormRegister<QueryState>;
  readonly setValue: UseFormSetValue<QueryState>;
  readonly showSearch?: boolean;
  readonly site: SiteEnum;
  readonly subtitle?: string;
  readonly title?: string;
  readonly type: ListingTypeEnum;
  readonly handleFormSubmit: () => void;
  readonly onAutoCompleteSubmit: (value: string) => void;
  readonly setCleanQueryParams: (data: QueryState) => void;
}

const ListingHeader = ({
  autoCompleteEndpoint,
  autoCompletePlaceholder,
  errors,
  handleFormSubmit,
  initialSearchText,
  listingFilterParam,
  listingFilters,
  onAutoCompleteSubmit,
  queryState,
  register,
  setCleanQueryParams,
  setValue,
  showSearch,
  site,
  subtitle,
  title,
  type,
}: ListingHeaderProps) => {
  const { clearSearch } = useListingState();
  const dispatch = useListingDispatch();

  const handleListingFiltersUpdate = (
    selectedListingFilters: IListingFilterGroup[]
  ) => {
    const currentIds = listingFilterParam
      ? queryState[listingFilterParam] ?? []
      : [];
    const selectedIds = selectedListingFilters.map(({ filter }) =>
      filter.id.toString()
    );

    // Preserve order ID selection in the query params
    const updatedIds = [
      ...currentIds.filter((id: string) => selectedIds.includes(id)),
      ...selectedIds.filter((id) => !currentIds.includes(id)),
    ];

    dispatch({
      type: ActionType.SORTED_LISTING_FILTERS,
      payload: selectedListingFilters,
    });

    setCleanQueryParams({
      ...queryState,
      ...(listingFilterParam ? { [listingFilterParam]: updatedIds } : {}),
      page: 0,
      skip: 0,
    });
  };

  return (
    <div
      className={classNames('tw-pb-xl md:tw-pb-2xl xl:tw-pb-xl', {
        'tw-bg-white': type !== ListingTypeEnum.Inspiration,
      })}
    >
      <div
        className="tw-container tw-grid tw-grid-cols-4 tw-gap-x-xs tw-gap-y-md tw-pb-md tw-pt-xl tw-text-black md:tw-grid-cols-6 md:tw-gap-x-md md:tw-gap-y-xl md:tw-pb-2xl lg:tw-grid-cols-12
xl:tw-pt-2xl "
      >
        {title && (
          <div className="tw-col-span-full tw-flex tw-flex-col tw-items-center tw-gap-y-md tw-text-center md:tw-gap-y-lg xl:tw-col-span-6 xl:tw-col-start-4">
            <Text variant="heading-56" className="tw-hidden md:tw-block">
              {title}
            </Text>
            <Text variant="heading-32" className="tw-block md:tw-hidden">
              {title}
            </Text>
            {subtitle && (
              <Text
                variant="body"
                className="tw-rich-text tw-text-grey-80 md:tw-mb-0"
                dangerouslySetInnerHTML={{ __html: subtitle }}
              />
            )}
          </div>
        )}
        {showSearch && (
          <div className="tw-col-span-full tw-col-start-1 tw-flex-col tw-gap-xs md:tw-col-span-4 md:tw-col-start-2 md:tw-flex lg:tw-col-start-5">
            <form onSubmit={handleFormSubmit}>
              <Search
                clearSearch={clearSearch}
                autoCompleteEndpoint={autoCompleteEndpoint}
                setValue={setValue as any}
                errors={errors}
                label={'Search'}
                name={'freetext'}
                initialValue={initialSearchText ?? ''}
                register={register as any}
                placeholder={autoCompletePlaceholder}
                onAutoCompleteSelect={onAutoCompleteSubmit}
              />
            </form>
          </div>
        )}
      </div>
      {!!listingFilters?.length && (
        <ListingFilters
          type={type}
          filters={listingFilters}
          selectedListingFilters={
            listingFilterParam ? queryState[listingFilterParam] ?? [] : []
          }
          onClickCallback={(selectedListingFilters) => {
            handleListingFiltersUpdate(selectedListingFilters);
          }}
          site={site}
        />
      )}
    </div>
  );
};

export default ListingHeader;
